import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const MoneyOut = () => {
  const [invoices, setInvoices] = useState([]);
  const [vendorDetails, setVendorDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [processing, setProcessing] = useState({});
  const navigate = useNavigate();

  // Get logged in user
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    // Only allow admin (user_type == 2)
    if (user.user_type !== 2) {
      navigate('/unauthorized');
      return;
    }
    fetchInvoices();
    // eslint-disable-next-line
  }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    setMessage('');
    try {
  const res = await fetch(`${process.env.REACT_APP_VENDOR_DOMAIN}/api/allinvoices?admin=true`);
      const data = await res.json();
      if (res.ok && data.invoices) {
        setInvoices(data.invoices);
        // Fetch vendor details for each unique vendor_id
        const vendorIds = Array.from(new Set(data.invoices.map(inv => inv.vendor_id)));
        const detailsMap = {};
        await Promise.all(
          vendorIds.map(async (vendorId) => {
            try {
              const vRes = await fetch(`${process.env.REACT_APP_VENDOR_DOMAIN}/vendor?vendor_id=${vendorId}`);
              const vData = await vRes.json();
              if (vRes.ok && vData.vendor) {
                detailsMap[vendorId] = vData.vendor;
              }
            } catch {}
          })
        );
        setVendorDetails(detailsMap);
      } else {
        setMessage(data.message || 'Failed to fetch invoices');
      }
    } catch {
      setMessage('Error fetching invoices');
    }
    setLoading(false);
  };

  const handleMoneySent = async (invoiceId) => {
    setProcessing(prev => ({ ...prev, [invoiceId]: true }));
    setMessage('');
    try {
      // Find the vendor_id for this invoice
      const invoice = invoices.find(inv => inv.id === invoiceId);
      if (!invoice) throw new Error('Invoice not found');
      const vendor_id = invoice.vendor_id;
      // Call the backend API to set money_sent for this vendor
  const res = await fetch(`${process.env.REACT_APP_VENDOR_DOMAIN}/api/moneysent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoice_id: invoiceId})
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setMessage('Money sent for invoice #' + invoiceId + ' of vendor #' + vendor_id);
        // Optionally, refresh invoice data
        fetchInvoices();
      } else {
        setMessage(data.message || 'Failed to update money sent');
      }
    } catch (err) {
      setMessage('Error processing money sent');
    }
    setProcessing(prev => ({ ...prev, [invoiceId]: false }));
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    // Convert to US Eastern Time (handles EST/EDT automatically)
    const options = {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    };
    // Format: MM/DD/YYYY HH:mm:ss (EST/EDT)
    const parts = new Intl.DateTimeFormat('en-US', options).formatToParts(d);
    const get = (type) => parts.find(p => p.type === type)?.value || '';
    return `${get('month')}/${get('day')}/${get('year')} ${get('hour')}:${get('minute')}:${get('second')}`;
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Admin Money Out</h2>
      {message && <div style={styles.message}>{message}</div>}
      {loading ? (
        <div style={styles.loading}>Loading invoices...</div>
      ) : invoices.length === 0 ? (
        <div style={styles.empty}>No invoices found.</div>
      ) : (
        <div style={styles.card}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Invoice #</th>
                <th style={styles.th}>First Name</th>
                <th style={styles.th}>Last Name</th>
                <th style={styles.th}>Email</th>
                <th style={styles.th}>Amount</th>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Cash Out Requested</th>
                <th style={styles.th}>Money Sent</th>
                <th style={styles.th}>Money Sent Date</th>
                <th style={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {invoices.filter(inv => inv.cashed_out).map((inv, idx) => {
                const vendor = vendorDetails[inv.vendor_id] || {};
                return (
                  <tr key={inv.id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                    <td style={styles.td}>{inv.id}</td>
                    <td style={styles.td}>{vendor.firstName || ''}</td>
                    <td style={styles.td}>{vendor.lastName || ''}</td>
                    <td style={styles.td}>{vendor.email || ''}</td>
                    <td style={styles.td}>${(inv.amount / 1.1).toFixed(2)}</td>
                    <td style={styles.td}>{formatDateTime(inv.date)}</td>
                    <td style={styles.td}>{inv.cashed_out ? 'Yes' : 'No'}</td>
                    <td style={styles.td}>{inv.money_sent ? 'Yes' : 'No'}</td>
                    <td style={styles.td}>{inv.money_sent_date ? formatDateTime(inv.money_sent_date) : ''}</td>
                    <td style={styles.td}>
                      {!inv.money_sent && (
                        <button
                          style={styles.moneySentBtn}
                          onClick={() => handleMoneySent(inv.id)}
                          disabled={processing[inv.id]}
                        >
                          {processing[inv.id] ? 'Processing...' : 'Money Sent'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1100px',
    margin: '40px auto',
    padding: '32px',
    background: 'linear-gradient(120deg, #f8fafc 0%, #e3e6f3 100%)',
    borderRadius: '18px',
    boxShadow: '0 4px 24px rgba(25,118,210,0.13)',
    minHeight: '80vh',
  },
  card: {
    background: '#fff',
    borderRadius: '14px',
    boxShadow: '0 2px 12px rgba(25,118,210,0.10)',
    padding: '18px 12px 24px 12px',
    marginTop: '18px',
    overflowX: 'auto',
  },
  title: {
    color: '#1976d2',
    fontSize: '2.2rem',
    fontWeight: 'bold',
    marginBottom: '28px',
    textAlign: 'center',
    letterSpacing: '1px',
    textShadow: '0 2px 8px #e3e6f3',
  },
  message: {
    background: '#fffde7',
    color: '#8d6e36',
    padding: '14px',
    borderRadius: '10px',
    marginBottom: '22px',
    textAlign: 'center',
    fontWeight: 'bold',
    border: '1.5px solid #ffe082',
    fontSize: '1.1rem',
    boxShadow: '0 1px 4px #ffe08244',
  },
  loading: {
    textAlign: 'center',
    color: '#1976d2',
    fontSize: '1.2rem',
    marginTop: '40px',
    fontWeight: 'bold',
    letterSpacing: '0.5px',
  },
  empty: {
    textAlign: 'center',
    color: '#666',
    fontSize: '1.1rem',
    padding: '40px',
    fontStyle: 'italic',
    background: '#fff',
    borderRadius: '10px',
    marginTop: '30px',
    boxShadow: '0 1px 4px #e3e6f3',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    marginTop: '10px',
    fontSize: '1.05rem',
    background: '#fff',
    borderRadius: '10px',
    overflow: 'hidden',
    boxShadow: '0 1px 8px #e3e6f3',
  },
  th: {
    background: 'linear-gradient(90deg, #e3e6f3 0%, #f8fafc 100%)',
    color: '#1976d2',
    fontWeight: 'bold',
    padding: '14px 10px',
    borderBottom: '2px solid #d8e0f3',
    position: 'sticky',
    top: 0,
    zIndex: 2,
    fontSize: '1.05rem',
    letterSpacing: '0.5px',
    textAlign: 'left',
  },
  td: {
    padding: '12px 10px',
    borderBottom: '1px solid #f0f0f0',
    color: '#333',
    background: 'transparent',
    fontSize: '1.01rem',
    verticalAlign: 'middle',
    textAlign: 'left',
  },
  trEven: {
    background: '#f8fafc',
    transition: 'background 0.2s',
  },
  trOdd: {
    background: '#fff',
    transition: 'background 0.2s',
  },
  moneySentBtn: {
    background: 'linear-gradient(90deg, #388e3c 0%, #43a047 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 22px',
    fontWeight: 'bold',
    fontSize: '1rem',
    cursor: 'pointer',
    boxShadow: '0 1px 4px #c8e6c9',
    transition: 'background 0.2s, box-shadow 0.2s',
    outline: 'none',
    margin: '0 2px',
  },
};

export default MoneyOut;
